/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ForwardRefExoticComponent, HTMLAttributes, ReactNode, RefAttributes } from 'react';
import { forwardRef, useEffect, useRef } from 'react';

import { cssMap, css as cssUnbounded, keyframes } from '@compiled/react';
import FocusLock from 'react-focus-lock';

import { css, jsx } from '@atlaskit/css';
import type { EditorAppearance } from '@atlaskit/editor-common/types';
import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import { token } from '@atlaskit/tokens';
import {
	RovoGradientKeyframesStyle,
	ROVO_GRADIENT_BACKGROUND,
} from '@atlassian/rovo-platform-ui-components/rovo-gradient';

import type { PaddingAdjustments } from './types';

type Props = {
	appearance?: EditorAppearance;
	children: ReactNode;
	disablePadding?: boolean;
	enableMaxHeight?: boolean;
	enableMaxWidth?: boolean;
	isAIFC?: boolean;
	/**
	 * If true, this container is only used for positioning
	 * and will not apply padding, background, borders, etc.
	 */
	isPositionalOnly?: boolean;
	paddingAdjustments?: PaddingAdjustments;
	rainbowBorder?: boolean;
	rainbowBorderAnimation?: boolean;
	rovoGradientBorder?: boolean;
} & HTMLAttributes<HTMLDivElement>;

const isFirefox: boolean =
	typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().indexOf('firefox') > -1;

const PRISM_BORDER_STEP_1 = '#0065FF';
const PRISM_BORDER_STEP_2 = '#0469FF';
const PRISM_BORDER_STEP_3 = '#BF63F3';
const PRISM_BORDER_STEP_4 = '#FFA900';

const defaultPadding = { top: 12, bottom: 12, left: 12, right: 12 };

// ED-20633, DSP-14045: Avoid space tokens for now as space tokens are currently using rem units
// and Safari has a bug where rem is incorrectly calculated when there are
// animations involved
// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const prismBorderStyles = cssUnbounded({
	'@property --gradient-angle': { syntax: "'<angle>'", initialValue: '270deg', inherits: 'false' },
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/platform/no-module-level-eval
	borderRadius: token('radius.large'),
	boxShadow: 'none',
	'&::before, &::after': {
		content: "''",
		position: 'absolute',
		zIndex: -1,
		width: 'calc(100% + 2px)',
		height: 'calc(100% + 2px)',
		top: '-1px',
		left: '-1px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/platform/no-module-level-eval
		borderRadius: `calc(${token('radius.large')} + 1px)`,
		transform: 'translate3d(0, 0, 0)',
	},
	'&::after': { right: '1px', bottom: '1px', filter: 'blur(8px)', opacity: 0.5 },
	'@media (prefers-reduced-motion)': { animation: 'none' },
});

const newPrismBorderStyles = cssUnbounded({
	'@property --gradient-angle': { syntax: "'<angle>'", initialValue: '270deg', inherits: 'false' },
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/platform/no-module-level-eval
	borderRadius: '1rem',
	boxShadow: 'none',
	'&::before, &::after': {
		content: "''",
		position: 'absolute',
		zIndex: -1,
		width: 'calc(100% + 2px)',
		height: 'calc(100% + 2px)',
		top: '-1px',
		left: '-1px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/platform/no-module-level-eval
		borderRadius: `calc(1rem + 1px)`,
		transform: 'translate3d(0, 0, 0)',
	},
	'&::after': { right: '1px', bottom: '1px', filter: 'blur(8px)', opacity: 0.5 },
	'@media (prefers-reduced-motion)': { animation: 'none' },
});

const staticStyles = cssMap({
	default: {
		'&::before, &::after': {
			background: `conic-gradient(
				from var(--gradient-angle, 270deg),
				${PRISM_BORDER_STEP_1} 0%,
				${PRISM_BORDER_STEP_2} 20%,
				${PRISM_BORDER_STEP_3} 50%,
				${PRISM_BORDER_STEP_4} 56%,
				${PRISM_BORDER_STEP_1} 100%
			)`,
		},
	},
	firefox: {
		'&::before, &::after': {
			background: `linear-gradient(90deg,
			${PRISM_BORDER_STEP_1} 0%,
			${PRISM_BORDER_STEP_2} 12%,
			${PRISM_BORDER_STEP_3} 24%,
			${PRISM_BORDER_STEP_4} 48%,
			${PRISM_BORDER_STEP_3} 64%,
			${PRISM_BORDER_STEP_2} 80%,
			${PRISM_BORDER_STEP_1} 100%
			)`,
			backgroundSize: '200%',
		},
	},
});

const fireFoxBgPosition = keyframes({
	from: { backgroundPosition: '100%' },
	to: { backgroundPosition: '-100%' },
});

const rotationAnimation = keyframes({
	// @ts-ignore - error TS2769: No overload matches this call.
	from: { '--gradient-angle': '0deg' },
	// @ts-ignore - error TS2769: No overload matches this call.
	to: { '--gradient-angle': '360deg' },
});

const animationStyles = cssMap({
	default: {
		'&::before, &::after': {
			animationName: rotationAnimation,
			animationDuration: '2s',
			animationTimingFunction: 'linear',
			animationIterationCount: 'infinite',
		},
	},
	firefox: {
		'&::before, &::after': {
			animationName: fireFoxBgPosition,
			animationDuration: '1s',
			animationTimingFunction: 'linear',
			animationIterationCount: 'infinite',
			animationDirection: 'normal',
		},
	},
});

const rovoMosaicBorderStyles = cssUnbounded({
	position: 'relative',
	borderRadius: token('radius.xxlarge'),
	boxShadow: 'none',
	'&::before, &::after': {
		content: "''",
		position: 'absolute',
		zIndex: -1,
		width: 'calc(100% + 4px)',
		height: 'calc(100% + 4px)',
		top: '-2px',
		left: '-2px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		borderRadius: `calc(${token('radius.xxlarge')} + 2px)`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- gradient background not expressible in cssMap
		background: ROVO_GRADIENT_BACKGROUND,
		animationName: rotationAnimation,
		animationDuration: '2s',
		animationTimingFunction: 'linear',
		animationIterationCount: 'infinite',
	},
	'@media (prefers-reduced-motion)': {
		'&::before, &::after': { animation: 'none' },
	},
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const constantStyles = cssUnbounded({
	boxShadow: token('elevation.shadow.overlay'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/platform/no-module-level-eval
	borderRadius: token('radius.large'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'> :last-child': { borderBottomLeftRadius: 'inherit', borderBottomRightRadius: 'inherit' },
	height: '100%',
	width: '100%',
	boxSizing: 'border-box',
	backgroundColor: token('elevation.surface.overlay'),
});

const newConstantStyles = cssUnbounded({
	boxShadow: token('elevation.shadow.overlay'),
	borderRadius: token('radius.xxlarge'),
	// don't define height here as it will affect the unhandled error modal height if it's caught by the outer error boundary (there are two error boundaries in AI...)
	width: '100%',
	boxSizing: 'border-box',
	backgroundColor: token('elevation.surface.overlay'),
});

const maxHeightCSS = css({ maxHeight: '500px' });

const centeringCSS = css({
	margin: '0 auto',
	scrollMarginTop: token('space.400'),
});

const fitContentWidthCSS = css({
	width: 'fit-content',
});

const maxWidthCSS = css({
	width: '400px',
	maxWidth: '400px',
	justifySelf: 'center',
});

// EDITOR-3718: Responsive width fix for narrow screens.
// Clamp to 400px or the container width. Set both `width` (preferred/base size)
// and `maxWidth` (hard cap + overrides existing rules) to keep sizing consistent.
const responsiveMaxWidthOverrideCSS = css({
	width: 'min(400px, 100%)',
	maxWidth: 'min(400px, 100%)',
	boxSizing: 'border-box',
});

/**
 * FocusLock adds a focus trap so that users can keep tabbing through the
 * command palette without leaving it unless when pressing escape or clicking outside.
 */
const GatedFocusLock = ({ children, isAIFC }: { children: ReactNode; isAIFC?: boolean }) => {
	const scrollParentRef = useRef<HTMLElement>();
	const ref = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (ref.current) {
			scrollParentRef.current = findOverflowScrollParent(ref.current) || undefined;
		}
	}, []);
	if (!isAIFC) {
		return (
			<FocusLock
				ref={ref}
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				css={{ width: '100%' }}
				noFocusGuards
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				returnFocus={{ preventScroll: true }}
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				whiteList={(activeElement) => {
					if (activeElement === scrollParentRef.current) {
						return false;
					}
					return true;
				}}
				/**
				 * focusOptions is deprecated but based on docs, it can still be used.
				 * We need to prevent scroll when auto-focusing to prevent issues with
				 * modal positioning.
				 * Modal will now always be on screen - it will be docked to top or bottom if
				 * it is out of viewport.
				 */
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				focusOptions={{ preventScroll: true }}
			>
				{children}
			</FocusLock>
		);
	}
	return children;
};

export const FloatingContainer: ForwardRefExoticComponent<
	{
		appearance?: EditorAppearance;
		children: ReactNode;
		disablePadding?: boolean;
		enableMaxHeight?: boolean;
		enableMaxWidth?: boolean;
		isAIFC?: boolean;
		isPositionalOnly?: boolean;
		paddingAdjustments?: PaddingAdjustments;
		rainbowBorder?: boolean;
		rainbowBorderAnimation?: boolean;
		rovoGradientBorder?: boolean;
	} & HTMLAttributes<HTMLDivElement> &
		RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, Props>(
	(
		{
			appearance,
			paddingAdjustments,
			rainbowBorder = true,
			rainbowBorderAnimation,
			rovoGradientBorder,
			disablePadding,
			children,
			enableMaxHeight,
			enableMaxWidth,
			className,
			style,
			isAIFC,
			isPositionalOnly,
			...rest
		}: Props,
		ref: React.Ref<HTMLDivElement>,
	) => {
		return (
			<GatedFocusLock isAIFC={isAIFC}>
				<div
					ref={ref}
					css={[
						!isPositionalOnly && rainbowBorder && !isAIFC && prismBorderStyles,
						!isPositionalOnly && rainbowBorder && isAIFC && newPrismBorderStyles,
						!isPositionalOnly && rovoGradientBorder && rovoMosaicBorderStyles,
						!isPositionalOnly &&
							!rovoGradientBorder &&
							staticStyles[isFirefox ? 'firefox' : 'default'],
						!isPositionalOnly && isAIFC && newConstantStyles,
						!isPositionalOnly && !isAIFC && constantStyles,
						!isPositionalOnly &&
							rainbowBorderAnimation &&
							animationStyles[isFirefox ? 'firefox' : 'default'],
						// maxHeight for command palette (especially when there are lots of user input)
						// It is a reasonable number for tickets EDF-1452, EDF-1572, EDF-1566
						// If you update this value, please also update the maxHeight value in Suggestions.tsx and test file editor-plugin-ai/src/__tests__/playwright/suggestions/scrollbar.spec.ts
						// when promptBuilder is removed, perhaps move this into css to keep it as a default style
						enableMaxHeight ? maxHeightCSS : null,
						enableMaxWidth ? maxWidthCSS : null,
						enableMaxWidth ? responsiveMaxWidthOverrideCSS : null,
						isAIFC ? centeringCSS : null,
						isAIFC && !enableMaxWidth ? fitContentWidthCSS : null,
					]}
					style={{
						...style,
						...(disablePadding || isPositionalOnly
							? null
							: paddingAdjustments
								? {
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
										paddingTop: `${defaultPadding.top + (paddingAdjustments.top ?? 0)}px`,
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
										paddingRight: `${defaultPadding.right + (paddingAdjustments.right ?? 0)}px`,
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
										paddingBottom: `${defaultPadding.bottom + (paddingAdjustments.bottom ?? 0)}px`,
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
										paddingLeft: `${defaultPadding.left + (paddingAdjustments.left ?? 0)}px`,
									}
								: { padding: `${defaultPadding.top}px ${defaultPadding.left}px` }),
					}}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
					className={className}
					{...rest}
				>
					{!isPositionalOnly && rovoGradientBorder && <RovoGradientKeyframesStyle />}
					{children}
				</div>
			</GatedFocusLock>
		);
	},
);
